// Earth body tree — the proof-of-depth for the matryoshka info system.
//
// Schema (recursive):
//   { id, label, summary?, payload?, children? }
//
// `payload` describes WHAT to render inside this panel above its children.
// Supported payload types: 'description', 'stats', 'composition-bars',
// 'list', 'links', 'callout'. Adding a new type means handling it in
// src/ui/matryoshka.js renderPayload().

export const earthTree = {
  id: 'earth',
  label: 'Earth',
  type: 'Terrestrial Planet',
  summary: 'The third planet from the Sun and the only world known to harbor life.',
  payload: [
    {
      type: 'description',
      text: 'Earth formed roughly 4.54 billion years ago from the same protoplanetary disk that built the rest of the solar system. About 71% of its surface is liquid water, the only world where that\'s true today. A magnetic dynamo in its outer core, plate tectonics, and a substantial atmosphere combine to make it the only place in the universe known to support life.'
    },
    {
      type: 'stats',
      label: 'At a glance',
      entries: [
        { label: 'Diameter',          value: '12,742 km' },
        { label: 'Mass',              value: '5.972 × 10²⁴ kg' },
        { label: 'Distance from Sun', value: '149.6 M km (1 AU)' },
        { label: 'Orbital period',    value: '365.256 days (sidereal)' },
        { label: 'Day length',        value: '23h 56m 4s (sidereal)' },
        { label: 'Moons',             value: '1 (Luna)' },
        { label: 'Atmosphere',        value: '78% N₂, 21% O₂' },
        { label: 'Mean surface temp', value: '15 °C / 288 K' }
      ]
    }
  ],
  children: [
    {
      id: 'vital-stats',
      label: 'Vital statistics',
      summary: 'Mass, gravity, magnetic field, internal structure',
      payload: [
        {
          type: 'stats',
          label: 'Physical',
          entries: [
            { label: 'Equatorial radius', value: '6,378.137 km' },
            { label: 'Polar radius',      value: '6,356.752 km' },
            { label: 'Oblateness',        value: '0.00335 (1/298.257)' },
            { label: 'Volume',            value: '1.083 × 10¹² km³' },
            { label: 'Mean density',      value: '5.514 g/cm³' },
            { label: 'Surface gravity',   value: '9.807 m/s² (1 g)' },
            { label: 'Escape velocity',   value: '11.186 km/s' }
          ]
        },
        {
          type: 'stats',
          label: 'Magnetic',
          entries: [
            { label: 'Field strength', value: '25–65 µT at surface' },
            { label: 'Dipole tilt',    value: '~9.4° from rotation axis (2020)' },
            { label: 'Magnetopause',   value: '~10 R⊕ sunward' },
            { label: 'Source',         value: 'Liquid iron outer core dynamo' }
          ]
        },
        {
          type: 'callout',
          tone: 'info',
          text: 'Earth\'s magnetic field has reversed 183 times in the last 83 million years, on average about every 300,000 years, though the gaps vary widely. The last full reversal (Brunhes-Matuyama) was ~780,000 years ago, and no one can say when the next will come.'
        }
      ]
    },

    {
      id: 'atmosphere',
      label: 'Atmosphere',
      summary: 'Five layers, dominated by nitrogen and oxygen, drives weather and shields life from radiation',
      payload: [
        {
          type: 'description',
          text: 'Earth\'s atmosphere is a thin gaseous envelope held by gravity. By mass it\'s about 5.15 × 10¹⁸ kg, roughly one-millionth of Earth\'s total mass. Pressure at sea level averages 101.325 kPa.'
        }
      ],
      children: [
        {
          id: 'composition',
          label: 'Composition',
          summary: 'Gas mix by volume in dry air',
          payload: [
            {
              type: 'composition-bars',
              entries: [
                { name: 'Nitrogen (N₂)',     percent: 78.084,  color: '#4a9eff' },
                { name: 'Oxygen (O₂)',       percent: 20.946,  color: '#7dd3a8' },
                { name: 'Argon (Ar)',        percent: 0.9340,  color: '#c099ff' },
                { name: 'Carbon dioxide (CO₂)', percent: 0.0420, color: '#ff9466' },
                { name: 'Neon (Ne)',         percent: 0.001818, color: '#ffd966' },
                { name: 'Helium (He)',       percent: 0.000524, color: '#ffaaaa' },
                { name: 'Methane (CH₄)',     percent: 0.000194, color: '#aa6644' },
                { name: 'Water vapor (H₂O)', percent: 1,       color: '#88ccff', note: 'highly variable, typically about 1%' }
              ]
            },
            {
              type: 'callout',
              tone: 'warn',
              text: 'CO₂ has risen from ~280 ppm pre-industrial to over 420 ppm today. The composition above is the contemporary measurement.'
            }
          ]
        },

        {
          id: 'layers',
          label: 'Vertical structure',
          summary: 'Troposphere through exosphere, defined by temperature inversions',
          children: [
            {
              id: 'troposphere',
              label: 'Troposphere',
              summary: '0–12 km. Where weather happens.',
              payload: [
                { type: 'description', text: 'Holds ~75% of atmospheric mass and nearly all water vapor. Temperature drops with altitude (lapse rate ~6.5 °C/km).' },
                { type: 'stats', entries: [
                  { label: 'Top altitude',  value: '~12 km (varies 8 km at poles, 18 km at equator)' },
                  { label: 'Mass fraction', value: '~75%' },
                  { label: 'Top temperature', value: '−55 °C (218 K)' }
                ]}
              ]
            },
            {
              id: 'stratosphere',
              label: 'Stratosphere',
              summary: '12–50 km. Where the ozone layer lives.',
              payload: [
                { type: 'description', text: 'Temperature INCREASES with altitude here because ozone absorbs UV. Commercial aircraft cruise in the lower stratosphere.' },
                { type: 'stats', entries: [
                  { label: 'Top altitude',     value: '~50 km' },
                  { label: 'Top temperature',  value: '−3 °C (270 K)' },
                  { label: 'Ozone peak',       value: '~25–30 km' }
                ]}
              ]
            },
            {
              id: 'mesosphere',
              label: 'Mesosphere',
              summary: '50–85 km. Where meteors burn up.',
              payload: [
                { type: 'description', text: 'Coldest layer. Temperature drops to about −90 °C at the mesopause. Most meteors ablate here.' },
                { type: 'stats', entries: [
                  { label: 'Top altitude',     value: '~85 km' },
                  { label: 'Top temperature',  value: '−90 °C (183 K), the coldest in Earth\'s atmosphere' }
                ]}
              ]
            },
            {
              id: 'thermosphere',
              label: 'Thermosphere',
              summary: '85–600 km. Where the ISS orbits and aurorae glow.',
              payload: [
                { type: 'description', text: 'Temperature soars with altitude (up to 2,500 °C in daytime) because solar UV heats sparse gas. The ISS orbits at ~420 km, well inside this layer.' },
                { type: 'stats', entries: [
                  { label: 'ISS altitude',       value: '~420 km' },
                  { label: 'Daytime peak temp',  value: '500–2,500 °C' },
                  { label: 'Aurorae',            value: '100–300 km' }
                ]}
              ]
            },
            {
              id: 'exosphere',
              label: 'Exosphere',
              summary: '600–10,000 km. The fade into space.',
              payload: [
                { type: 'description', text: 'The outermost layer where atoms can escape into space. Hydrogen and helium dominate. There is no clear upper boundary; it gradually merges with the solar wind.' },
                { type: 'stats', entries: [
                  { label: 'Top',         value: '~10,000 km (geocorona detected to ~600,000 km)' },
                  { label: 'Karman line', value: '100 km, the official "edge of space"' }
                ]}
              ]
            }
          ]
        },

        {
          id: 'greenhouse',
          label: 'Greenhouse effect',
          summary: 'How the atmosphere keeps Earth ~33 °C warmer than it would otherwise be',
          payload: [
            {
              type: 'description',
              text: 'Without an atmosphere, Earth\'s mean surface temperature would be about −18 °C. The greenhouse effect (the re-emission of infrared radiation by water vapor, CO₂, methane, and other trace gases) keeps it at about +15 °C.'
            },
            {
              type: 'stats',
              label: 'Share of today\'s greenhouse effect (Schmidt et al. 2010)',
              entries: [
                { label: 'Water vapor', value: '~50%' },
                { label: 'Clouds',      value: '~25%' },
                { label: 'CO₂',         value: '~20%' },
                { label: 'Methane, N₂O, ozone, CFCs', value: '~5%' }
              ]
            }
          ]
        }
      ]
    },

    {
      id: 'orbit',
      label: 'Orbit & rotation',
      summary: 'Where Earth is right now and how it moves',
      payload: [
        {
          type: 'description',
          text: 'Earth orbits the Sun in a slightly elliptical path averaging 149.6 million km, very close to one astronomical unit, which the IAU fixed in 2012 at exactly 149,597,870.7 km. The orbital plane (ecliptic) is the reference plane for the rest of the solar system.'
        }
      ],
      children: [
        {
          id: 'live-position',
          label: 'Position right now',
          summary: 'Live distance, speed and season, from the viewer\'s clock',
          payload: [
            {
              type: 'stats',
              label: 'At the viewer\'s date and time',
              entries: [
                { label: 'Distance from the Sun', readout: 'distance-sun', body: 'earth', value: '~1 AU (0.983 to 1.017)' },
                { label: 'Sunlight takes',        readout: 'light-sun', body: 'earth', value: '8 min 19 s' },
                { label: 'Orbital speed',         readout: 'speed', body: 'earth', value: '29.78 km/s mean' },
                { label: 'Around the orbit',      readout: 'orbit-progress', body: 'earth', value: '' },
                { label: 'Season',                readout: 'season', body: 'earth', value: '' },
                { label: 'Next',                  readout: 'next-season', body: 'earth', value: '' }
              ]
            }
          ]
        },
        {
          id: 'orbital-elements',
          label: 'Orbital elements',
          summary: 'The six numbers that define Earth\'s orbit',
          payload: [
            {
              type: 'stats',
              label: 'Keplerian elements (J2000 epoch)',
              entries: [
                { label: 'Semi-major axis (a)',       value: '1.000 AU' },
                { label: 'Eccentricity (e)',          value: '0.0167' },
                { label: 'Inclination (i)',           value: '0.00005° (defines ecliptic)' },
                { label: 'Longitude of asc. node (Ω)', value: '−11.26°' },
                { label: 'Argument of periapsis (ω)', value: '114.21°' },
                { label: 'Mean anomaly (M, J2000)',   value: '357.52°' }
              ]
            }
          ]
        },
        {
          id: 'rotation',
          label: 'Rotation',
          summary: 'How long is a day, really',
          payload: [
            {
              type: 'description',
              text: 'A solar day is 24 hours. A SIDEREAL day, the time for Earth to rotate once relative to the stars, is 23h 56m 4.0905s. The 4-minute difference is because Earth has also moved along its orbit, so it needs to turn a tiny bit more to face the Sun again.'
            },
            {
              type: 'stats',
              entries: [
                { label: 'Solar day',       value: '24h 00m 00s' },
                { label: 'Sidereal day',    value: '23h 56m 4.0905s' },
                { label: 'Axial tilt',      value: '23.4393°' },
                { label: 'Equatorial speed',value: '465.1 m/s (1,674 km/h)' }
              ]
            }
          ]
        },
        {
          id: 'seasons',
          label: 'Seasons',
          summary: 'Why we have them, and when the next one starts',
          payload: [
            {
              type: 'description',
              text: 'Seasons exist because Earth\'s rotation axis is tilted 23.44° relative to its orbital plane. When the Northern Hemisphere is tilted toward the Sun (June solstice), it gets summer; the Southern Hemisphere gets winter. Six months later it\'s the opposite.'
            },
            {
              type: 'stats',
              label: 'Reference dates (approximate)',
              entries: [
                { label: 'March equinox',   value: '~Mar 20' },
                { label: 'June solstice',   value: '~Jun 21' },
                { label: 'September equinox', value: '~Sep 22' },
                { label: 'December solstice', value: '~Dec 21' }
              ]
            }
          ]
        }
      ]
    },

    {
      id: 'surface',
      label: 'Surface',
      summary: '71% water, 29% land. Continents, oceans, extreme highs and lows.',
      children: [
        {
          id: 'continents',
          label: 'Continents',
          summary: 'Seven landmasses, but only six are commonly inhabited',
          payload: [
            {
              type: 'list',
              entries: [
                { primary: 'Asia',         secondary: '44.6 M km², most populous (4.7 B people)' },
                { primary: 'Africa',       secondary: '30.4 M km², most countries (54)' },
                { primary: 'North America', secondary: '24.7 M km², spans Arctic to tropics' },
                { primary: 'South America', secondary: '17.8 M km², Amazon basin and Andes' },
                { primary: 'Antarctica',   secondary: '14.0 M km², covered by 1.9 km thick ice sheet' },
                { primary: 'Europe',       secondary: '10.2 M km², densest network of nations' },
                { primary: 'Australia/Oceania', secondary: '8.5 M km², the smallest continent' }
              ]
            }
          ]
        },
        {
          id: 'oceans',
          label: 'Oceans',
          summary: 'Five oceans hold ~97% of Earth\'s water',
          payload: [
            {
              type: 'list',
              entries: [
                { primary: 'Pacific',  secondary: '165.2 M km², largest, 10,935 m max depth' },
                { primary: 'Atlantic', secondary: '106.5 M km², second largest' },
                { primary: 'Indian',   secondary: '70.6 M km², warmest' },
                { primary: 'Southern', secondary: '20.3 M km², circles Antarctica' },
                { primary: 'Arctic',   secondary: '14.1 M km², smallest, mostly ice-covered' }
              ]
            },
            {
              type: 'stats',
              entries: [
                { label: 'Mean ocean depth', value: '3,682 m' },
                { label: 'Total water volume', value: '1.386 × 10⁹ km³' },
                { label: 'Ocean fraction by mass', value: '0.022% of Earth' }
              ]
            }
          ]
        },
        {
          id: 'extremes',
          label: 'Surface extremes',
          summary: 'The highest, deepest, hottest, coldest',
          payload: [
            {
              type: 'list',
              entries: [
                { primary: 'Highest land', secondary: 'Mt. Everest, 8,849 m above sea level' },
                { primary: 'Deepest point', secondary: 'Challenger Deep, 10,935 m below sea level' },
                { primary: 'Hottest recorded', secondary: 'Furnace Creek, Death Valley: 56.7 °C (1913)' },
                { primary: 'Coldest recorded', secondary: 'Vostok Station, Antarctica: −89.2 °C (1983)' },
                { primary: 'Wettest', secondary: 'Mawsynram, India: 11,872 mm/yr average' },
                { primary: 'Driest', secondary: 'Atacama Desert, Chile: <1 mm/yr in places' }
              ]
            }
          ]
        }
      ]
    },

    {
      id: 'moon',
      label: 'The Moon',
      summary: '3,474 km diameter, fifth-largest moon in the solar system, has its own deep tree',
      payload: [
        {
          type: 'description',
          text: 'Earth\'s only natural satellite, formed when a Mars-sized body called Theia collided with the proto-Earth ~4.5 billion years ago. The debris coalesced in orbit. The Moon is tidally locked, always showing the same face.'
        },
        {
          type: 'stats',
          entries: [
            { label: 'Diameter',     value: '3,474.8 km' },
            { label: 'Mass',         value: '7.346 × 10²² kg (0.0123 Earth)' },
            { label: 'Mean distance', value: '384,400 km' },
            { label: 'Orbital period', value: '27.32 days (sidereal)' },
            { label: 'Synodic period', value: '29.53 days (new moon to new moon)' },
            { label: 'Surface gravity', value: '1.62 m/s² (0.166 g)' }
          ]
        }
      ],
      children: [
        {
          id: 'moon-formation',
          label: 'Origin: the Theia impact',
          summary: 'How the Moon was made',
          payload: [
            {
              type: 'description',
              text: 'The leading hypothesis (giant-impact theory) holds that a Mars-sized body collided with the early Earth roughly 4.51 billion years ago. The collision ejected a vast amount of mantle material into orbit, which coalesced into the Moon. Evidence: the Moon\'s composition closely matches Earth\'s mantle, and isotope ratios are nearly identical.'
            }
          ]
        },
        {
          id: 'moon-surface',
          label: 'Surface',
          summary: 'Maria, highlands, craters',
          children: [
            {
              id: 'moon-maria',
              label: 'Maria',
              summary: 'The dark "seas" are basaltic lava plains',
              payload: [
                {
                  type: 'description',
                  text: 'The dark patches the eye picks out on the Moon are ancient basaltic lava flows that filled large impact basins 3–4 billion years ago. Giovanni Riccioli named them maria (seas) on his 1651 map, and the name stuck after they turned out to be dry.'
                },
                {
                  type: 'list',
                  entries: [
                    { primary: 'Mare Imbrium',    secondary: 'Sea of Showers, 1,146 km' },
                    { primary: 'Mare Tranquillitatis', secondary: 'Sea of Tranquility, Apollo 11 landing site' },
                    { primary: 'Mare Serenitatis',     secondary: 'Sea of Serenity, 707 km' },
                    { primary: 'Oceanus Procellarum',  secondary: 'Ocean of Storms, largest mare, 2,592 km' }
                  ]
                }
              ]
            },
            {
              id: 'moon-craters',
              label: 'Notable craters',
              summary: 'The biggest and most famous',
              payload: [
                {
                  type: 'list',
                  entries: [
                    { primary: 'Tycho',     secondary: '85 km, ~108 M years old, prominent ray system' },
                    { primary: 'Copernicus', secondary: '93 km, ~800 M years old, terraced walls' },
                    { primary: 'South Pole-Aitken Basin', secondary: '2,500 km, oldest and largest impact basin in solar system' }
                  ]
                }
              ]
            }
          ]
        },
        {
          id: 'apollo',
          label: 'Apollo program',
          summary: 'Six successful crewed landings 1969–1972',
          payload: [
            {
              type: 'description',
              text: 'Twelve people have walked on the Moon, all of them American men, all between 1969 and 1972. The Apollo program returned 382 kg of lunar samples and left behind retroreflectors still used today to measure the Earth-Moon distance to millimeter precision.'
            }
          ],
          children: [
            {
              id: 'apollo-11',
              label: 'Apollo 11',
              summary: 'July 20, 1969: the first crewed landing',
              payload: [
                {
                  type: 'description',
                  text: 'The lunar module Eagle landed at 20:17 UTC on July 20, 1969, and Neil Armstrong became the first human to step onto the Moon at 02:56 UTC on July 21 (still the evening of July 20 in the US), followed 19 minutes later by Buzz Aldrin. Michael Collins remained in lunar orbit aboard Columbia. They spent 21h 36m on the surface, collected 21.5 kg of samples, and left a plaque reading "We came in peace for all mankind."'
                },
                {
                  type: 'stats',
                  entries: [
                    { label: 'Landing site',   value: 'Mare Tranquillitatis (0.67° N, 23.47° E)' },
                    { label: 'Surface stay',   value: '21 hours 36 minutes' },
                    { label: 'EVA time',       value: '2 hours 31 minutes' },
                    { label: 'Samples returned', value: '21.5 kg' }
                  ]
                }
              ]
            },
            {
              id: 'apollo-12',
              label: 'Apollo 12',
              summary: 'November 1969: a pinpoint landing next to Surveyor 3',
              payload: [{ type: 'description', text: 'Demonstrated precision landing capability. Touched down 163 m from the unmanned Surveyor 3 probe (which had landed 2½ years earlier), retrieved parts of it for study.' }]
            },
            {
              id: 'apollo-13',
              label: 'Apollo 13',
              summary: 'April 1970: "Houston, we\'ve had a problem"',
              payload: [{ type: 'description', text: 'An oxygen tank exploded on the way to the Moon. The lunar landing was aborted; the crew used the Lunar Module as a lifeboat and returned safely. Considered NASA\'s "successful failure."' }]
            },
            {
              id: 'apollo-14',
              label: 'Apollo 14',
              summary: 'February 1971: Alan Shepard hits a golf ball',
              payload: [{ type: 'description', text: 'Landed in the Fra Mauro highlands, the original target of Apollo 13. Alan Shepard famously hit two golf balls on the lunar surface using a makeshift 6-iron.' }]
            },
            {
              id: 'apollo-15',
              label: 'Apollo 15',
              summary: 'July 1971: the first lunar rover',
              payload: [{ type: 'description', text: 'First mission to use the Lunar Roving Vehicle. Crew traveled up to 5 km from the lander, collected the famous "Genesis Rock," anorthosite dating to 4.1 billion years.' }]
            },
            {
              id: 'apollo-16',
              label: 'Apollo 16',
              summary: 'April 1972: the first landing in the lunar highlands',
              payload: [{ type: 'description', text: 'Explored the Descartes Highlands. Showed that the highlands were not volcanic in origin as previously suspected, but composed of impact-generated breccias.' }]
            },
            {
              id: 'apollo-17',
              label: 'Apollo 17',
              summary: 'December 1972: the last humans on the Moon (so far)',
              payload: [{ type: 'description', text: 'The only Apollo mission to include a scientist (geologist Harrison Schmitt). Longest stay (75 hours) and longest EVA total (22 hours). No human has returned to the lunar surface since.' }]
            }
          ]
        },
        {
          id: 'moon-tides',
          label: 'Tides & tidal locking',
          summary: 'Why the Moon shows one face, and why oceans bulge',
          payload: [
            {
              type: 'description',
              text: 'The Moon\'s gravity pulls Earth\'s oceans into two bulges (one facing the Moon, one opposite). Earth rotates through these bulges, producing two high tides per ~25-hour cycle. Conversely, Earth\'s gravity slowed the Moon\'s rotation until it became tidally locked, turning once per orbit and always showing the same near side.'
            },
            {
              type: 'callout',
              tone: 'info',
              text: 'The Moon recedes from Earth at 3.8 cm/year. In about 50 billion years, Earth\'s rotation will slow enough to be tidally locked to the Moon, though the Sun will become a red giant long before then.'
            }
          ]
        }
      ]
    },

    {
      id: 'life',
      label: 'Life',
      summary: 'The biosphere: the only known one',
      payload: [
        {
          type: 'description',
          text: 'Life on Earth has existed for at least 3.7 billion years. The biosphere encompasses all living organisms and the parts of Earth that support them. As of 2024, biologists have formally described about 1.7 million species, with estimates of total existing species ranging from 8 million to over 80 million.'
        }
      ],
      children: [
        {
          id: 'domains',
          label: 'Domains of life',
          summary: 'Three branches at the root of the tree',
          payload: [
            {
              type: 'list',
              entries: [
                { primary: 'Bacteria', secondary: 'Single-celled prokaryotes, no nucleus, ubiquitous' },
                { primary: 'Archaea',  secondary: 'Single-celled prokaryotes, distinct biochemistry, often extremophiles' },
                { primary: 'Eukarya',  secondary: 'Cells with nuclei. Includes all plants, animals, fungi, protists' }
              ]
            }
          ]
        },
        {
          id: 'extinctions',
          label: 'Mass extinctions',
          summary: 'The "Big Five"',
          payload: [
            {
              type: 'list',
              entries: [
                { primary: 'Ordovician–Silurian (~444 Mya)',   secondary: '~85% of species lost. Glaciation event.' },
                { primary: 'Late Devonian (~375–360 Mya)',     secondary: '~75% of species lost. Series of pulses.' },
                { primary: 'Permian–Triassic (~252 Mya)',      secondary: '"The Great Dying": ~96% of marine species lost. Largest extinction known.' },
                { primary: 'Triassic–Jurassic (~201 Mya)',     secondary: '~80% of species lost. Cleared the way for dinosaur dominance.' },
                { primary: 'Cretaceous–Paleogene (~66 Mya)',   secondary: 'Asteroid impact at Chicxulub. End of non-avian dinosaurs.' }
              ]
            },
            {
              type: 'callout',
              tone: 'warn',
              text: 'Many biologists argue we are currently in the early stages of a sixth mass extinction (the Holocene/Anthropocene extinction), driven by human activity.'
            }
          ]
        }
      ]
    },

    {
      id: 'missions',
      label: 'Missions & spacecraft',
      summary: 'What\'s in orbit, what\'s on the surface',
      children: [
        {
          id: 'iss',
          label: 'International Space Station',
          summary: 'Continuously crewed since November 2000',
          payload: [
            {
              type: 'stats',
              entries: [
                { label: 'Orbital altitude', value: '~420 km (varies)' },
                { label: 'Orbital period',   value: '~93 minutes' },
                { label: 'Orbits per day',   value: '~15.5' },
                { label: 'Speed',            value: '7.66 km/s' },
                { label: 'Mass',             value: '419,725 kg' },
                { label: 'Habitable volume', value: '388 m³' },
                { label: 'Permanent crew',   value: '7 (typical)' },
                { label: 'Continuous occupancy since', value: 'November 2, 2000' }
              ]
            }
          ]
        },
        {
          id: 'hubble',
          label: 'Hubble Space Telescope',
          summary: 'Operating since 1990',
          payload: [
            { type: 'stats', entries: [
              { label: 'Launched', value: 'April 24, 1990 (STS-31)' },
              { label: 'Altitude', value: '~538 km' },
              { label: 'Mirror diameter', value: '2.4 m' },
              { label: 'Wavelength range', value: '115 nm – 2.5 µm (UV/visible/near-IR)' }
            ]}
          ]
        },
        {
          id: 'satellites',
          label: 'Active satellites',
          summary: 'Thousands in orbit, growing fast',
          payload: [
            {
              type: 'description',
              text: 'In October 2026 CelesTrak listed about 16,600 active satellites. About two thirds are SpaceX Starlink communications satellites in low Earth orbit. Other major categories: Earth observation, navigation (GPS, Galileo, GLONASS, BeiDou), weather, and military.'
            },
            {
              type: 'callout',
              tone: 'info',
              text: 'Switch on Satellites under Explore to see every one of them where it is right now, drawn from the same CelesTrak data.'
            }
          ]
        }
      ]
    },

    {
      id: 'history',
      label: 'Earth\'s history',
      summary: '4.54 billion years in four eons, the last split into three eras',
      payload: [
        {
          type: 'list',
          entries: [
            { primary: 'Hadean (4.54 – 4.0 Bya)',     secondary: 'Formation, magma ocean, lunar-forming impact, late heavy bombardment' },
            { primary: 'Archean (4.0 – 2.5 Bya)',     secondary: 'First continents, first life (cyanobacteria), oxygen begins accumulating' },
            { primary: 'Proterozoic (2.5 Bya – 539 Mya)', secondary: 'Great Oxidation Event, first eukaryotes, first multicellular life' },
            { primary: 'Paleozoic (539 – 252 Mya)',   secondary: 'Cambrian explosion, plants and animals colonize land, end-Permian extinction' },
            { primary: 'Mesozoic (252 – 66 Mya)',     secondary: 'Age of dinosaurs, first mammals, first birds, end-Cretaceous extinction' },
            { primary: 'Cenozoic (66 Mya – present)', secondary: 'Mammals diversify, primates evolve, humans emerge ~300,000 years ago' }
          ]
        }
      ]
    },

    {
      id: 'culture',
      label: 'Discovery & culture',
      summary: 'Earth was always known, but our concept of it has changed dramatically',
      payload: [
        {
          type: 'description',
          text: 'Earth is the only planet whose name in English does not derive from Greco-Roman mythology. The name comes from Old English "eorþe" and Old High German "erda," both meaning ground or soil. The first photograph of Earth from space was taken from a captured German V-2 rocket on October 24, 1946.'
        },
        {
          type: 'list',
          entries: [
            { primary: 'Spherical Earth (~6th century BCE)', secondary: 'Pythagoras and later Greeks deduced Earth\'s sphericity from observations' },
            { primary: 'Eratosthenes\' measurement (~240 BCE)', secondary: 'Calculated Earth\'s circumference within ~2% using shadow lengths' },
            { primary: 'Copernican heliocentrism (1543)', secondary: 'Earth becomes one planet among many, not the center' },
            { primary: 'Earthrise (1968)', secondary: 'Apollo 8 photograph, the first time humans saw Earth as a small blue marble in the void' },
            { primary: 'Pale Blue Dot (1990)', secondary: 'Voyager 1 photograph from 6 billion km away, showing Earth as a single bright pixel' }
          ]
        }
      ]
    },

    {
      id: 'references',
      label: 'External references',
      summary: 'Where to dig deeper',
      payload: [
        {
          type: 'links',
          entries: [
            { label: 'NASA Earth fact sheet',         url: 'https://nssdc.gsfc.nasa.gov/planetary/factsheet/earthfact.html' },
            { label: 'Wikipedia: Earth',              url: 'https://en.wikipedia.org/wiki/Earth' },
            { label: 'JPL Horizons (Earth ephemeris)', url: 'https://ssd.jpl.nasa.gov/horizons/' },
            { label: 'NOAA Space Weather Prediction Center', url: 'https://www.swpc.noaa.gov/' },
            { label: 'NOAA: how deep is the ocean', url: 'https://oceanservice.noaa.gov/facts/oceandepth.html' },
            { label: 'NASA: magnetic pole reversals', url: 'https://science.nasa.gov/science-research/earth-science/flip-flop-why-variations-in-earths-magnetic-field-arent-causing-todays-climate-change/' },
            { label: 'NASA GISS: greenhouse attribution', url: 'https://www.giss.nasa.gov/research/briefs/2010_schmidt_05/' },
            { label: 'JPL astrodynamic constants (au)', url: 'https://ssd.jpl.nasa.gov/astro_par.html' },
            { label: 'Apollo 11 timeline (NASA SP-4029)', url: 'https://history.nasa.gov/SP-4029/Apollo_11a_Summary.htm' },
            { label: 'ICS chronostratigraphic chart', url: 'https://stratigraphy.org/chart' },
            { label: 'Astronomy Picture of the Day', url: 'https://apod.nasa.gov/apod/' },
            { label: 'arXiv: search "Earth"',         url: 'https://arxiv.org/search/?searchtype=all&query=Earth+atmosphere' }
          ]
        }
      ]
    }
  ]
};
